import { collidesWithRegion } from './collision';
import type { Point } from './geometry';
import type { RegionDefinition, WorldMapDefinition } from './region-definition';
import { CURRENT_WORLD_MAP } from './regions/current-world';

export interface RegionRegistry {
  get(id: string): RegionDefinition | null;
  all(): readonly RegionDefinition[];
  regionAtWorldPoint(point: Point): RegionDefinition;
  worldToRegion(regionId: string, point: Point): Point;
  regionToWorld(regionId: string, point: Point): Point;
  clampPointToRegion(regionId: string, point: Point, radius: number): Point;
  nearestValidPoint(regionId: string, point: Point, radius: number): Point;
}

const SEARCH_STEP = 16;
const SEARCH_DIRECTIONS = 8;

function normalizeRadius(radius: number): number {
  return Number.isFinite(radius) && radius >= 0 ? radius : 0;
}

function centerOf(region: RegionDefinition): Point {
  return {
    x: region.bounds.x + region.bounds.w / 2,
    y: region.bounds.y + region.bounds.h / 2,
  };
}

function clampAxis(value: number, start: number, length: number, radius: number): number {
  const minimum = start + radius;
  const maximum = start + length - radius;
  if (minimum > maximum) return start + length / 2;
  return Math.max(minimum, Math.min(maximum, value));
}

export function createRegionRegistry(map: WorldMapDefinition): RegionRegistry {
  const regions = [...map.regions];
  const regionsById = new Map<string, RegionDefinition>();
  for (const region of regions) {
    if (regionsById.has(region.id)) throw new RangeError(`Duplicate region ID: ${region.id}`);
    regionsById.set(region.id, region);
  }

  const configuredFallback = regionsById.get(map.fallbackRegionId);
  if (!configuredFallback) throw new RangeError(`Missing fallback region: ${map.fallbackRegionId}`);
  const fallback: RegionDefinition = configuredFallback;
  const fallbackCenter = centerOf(fallback);

  function resolve(regionId: string): RegionDefinition {
    return regionsById.get(regionId) ?? fallback;
  }

  function clampPointToRegion(regionId: string, point: Point, radius: number): Point {
    const region = resolve(regionId);
    const normalizedRadius = normalizeRadius(radius);
    const x = Number.isFinite(point.x) ? point.x : fallbackCenter.x;
    const y = Number.isFinite(point.y) ? point.y : fallbackCenter.y;

    return {
      x: clampAxis(x, region.bounds.x, region.bounds.w, normalizedRadius),
      y: clampAxis(y, region.bounds.y, region.bounds.h, normalizedRadius),
    };
  }

  return {
    get(id) {
      return regionsById.get(id) ?? null;
    },
    all() {
      return [...regions];
    },
    regionAtWorldPoint(point) {
      return (
        regions.find(
          (region) =>
            point.x >= region.bounds.x &&
            point.x < region.bounds.x + region.bounds.w &&
            point.y >= region.bounds.y &&
            point.y < region.bounds.y + region.bounds.h,
        ) ?? fallback
      );
    },
    worldToRegion(regionId, point) {
      const region = resolve(regionId);
      return { x: point.x - region.bounds.x, y: point.y - region.bounds.y };
    },
    regionToWorld(regionId, point) {
      const region = resolve(regionId);
      return { x: point.x + region.bounds.x, y: point.y + region.bounds.y };
    },
    clampPointToRegion,
    nearestValidPoint(regionId, point, radius) {
      const region = resolve(regionId);
      const normalizedRadius = normalizeRadius(radius);
      const origin = clampPointToRegion(regionId, point, normalizedRadius);
      if (!collidesWithRegion(region, origin, normalizedRadius)) return origin;

      const ringCount = Math.floor(Math.max(region.bounds.w, region.bounds.h) / SEARCH_STEP);
      for (let ring = 1; ring <= ringCount; ring += 1) {
        const distance = ring * SEARCH_STEP;
        for (let direction = 0; direction < SEARCH_DIRECTIONS; direction += 1) {
          const angle = (direction * Math.PI * 2) / SEARCH_DIRECTIONS;
          const candidate = clampPointToRegion(
            region.id,
            {
              x: origin.x + Math.cos(angle) * distance,
              y: origin.y + Math.sin(angle) * distance,
            },
            normalizedRadius,
          );
          if (!collidesWithRegion(region, candidate, normalizedRadius)) return candidate;
        }
      }

      const fallbackPoint = clampPointToRegion(region.id, fallbackCenter, normalizedRadius);
      if (!collidesWithRegion(region, fallbackPoint, normalizedRadius)) return fallbackPoint;
      throw new RangeError('Region has no valid point');
    },
  };
}

export const regionRegistry: RegionRegistry = createRegionRegistry(CURRENT_WORLD_MAP);
